import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { startAuthentication } from '@simplewebauthn/browser'
import {
  adminLogin,
  adminLogout,
  adminLoginMode,
  passkeyLogin,
  passkeyLoginOptions,
} from '../lib/api'

/**
 * Anmeldung fürs Admin mit dem gemeinsamen Benutzer und Passwort des Teams
 * (ADR 0010, seit 20.10. der Regelweg). Sie erscheint **nur**, wenn die API
 * sagt, dass dieser Weg offen ist — läuft das Admin hinter Cloudflare Access,
 * ist die Anmeldung dort längst passiert und hier nichts zu zeigen.
 *
 * Bewusst kein Zustand im Browser-Speicher: Ob jemand angemeldet ist,
 * entscheidet allein das signierte Cookie, und das kann nur der Server setzen.
 * Alles andere wäre eine Anzeige, die sich fälschen lässt.
 */
export function LoginGate({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<'lädt' | 'offen' | 'passwort'>('lädt')
  const [user, setUser] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [passkeys, setPasskeys] = useState(false)

  useEffect(() => {
    void adminLoginMode()
      .then((r) => {
        setMode(r.passwordLogin ? 'passwort' : 'offen')
        // Passkeys nur anbieten, wo der Server sie annimmt und der Browser sie kann.
        setPasskeys(r.passkeyLogin === true && typeof window.PublicKeyCredential !== 'undefined')
      })
      // Antwortet der Endpunkt nicht, zeigen wir die Oberfläche: Die eigentliche
      // Prüfung passiert ohnehin serverseitig bei jedem Aufruf.
      .catch(() => setMode('offen'))
  }, [])

  useEffect(() => {
    if (mode !== 'passwort') return
    // Ein gültiges Cookie aus einer früheren Sitzung soll die Maske überspringen.
    void fetch('/api/v1/admin/config', { credentials: 'include' }).then((res) => {
      if (res.ok) setMode('offen')
    })
  }, [mode])

  if (mode === 'lädt') return <p className="p-8 text-text-muted">Lädt …</p>
  if (mode === 'offen') return <LogoutProvider>{children}</LogoutProvider>

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    void submit()
  }

  async function submit() {
    setBusy(true)
    setError(null)
    try {
      await adminLogin(user, password)
      setPassword('')
      setMode('offen')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Anmeldung fehlgeschlagen')
    } finally {
      setBusy(false)
    }
  }

  /**
   * Passkey: Optionen holen, das Gerät fragen (Face ID, Fingerabdruck,
   * Schlüssel), die signierte Antwort einreichen. Bricht der Benutzer ab,
   * ist das kein Fehler, nur kein Login — und das Passwort bleibt daneben.
   */
  async function mitPasskey() {
    setBusy(true)
    setError(null)
    try {
      const { options } = await passkeyLoginOptions()
      const response = await startAuthentication({ optionsJSON: options })
      await passkeyLogin(response)
      setMode('offen')
    } catch (err) {
      const text = err instanceof Error ? err.message : 'Anmeldung fehlgeschlagen'
      if (!/NotAllowedError|cancel|abgebrochen/i.test(text)) setError(text)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-4 sm:p-8">
      <form onSubmit={handleSubmit} className="card w-full max-w-sm space-y-5 p-6 sm:p-8">
        <div className="flex flex-col items-center gap-3 text-center">
          <img
            src="/brand/k4ia-logo.png"
            alt=""
            width={72}
            height={72}
            className="rounded-full shadow-[var(--shadow-card)]"
          />
          <h1 className="text-2xl">K4ia Admin</h1>
        </div>
        <label className="block space-y-1">
          <span className="text-sm">Benutzer</span>
          <input
            type="text"
            className="input w-full"
            autoComplete="username"
            autoCapitalize="none"
            value={user}
            onChange={(e) => setUser(e.target.value)}
            required
          />
        </label>
        <label className="block space-y-1">
          <span className="text-sm">Passwort</span>
          <input
            type="password"
            className="input w-full"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </label>
        {error && (
          <p role="alert" className="text-sm text-status-error">
            {error}
          </p>
        )}
        <button
          type="submit"
          className="btn-primary w-full"
          disabled={busy || user.trim() === '' || password === ''}
        >
          {busy ? 'Prüft …' : 'Anmelden'}
        </button>
        {passkeys && (
          <button
            type="button"
            className="btn-secondary w-full"
            onClick={() => void mitPasskey()}
            disabled={busy}
          >
            Mit Passkey anmelden
          </button>
        )}
      </form>
    </div>
  )
}

/**
 * Abmelden — je nach Anmeldeweg: Mit Passwort löscht der Server das Cookie;
 * hinter Cloudflare Access führt `/cdn-cgi/access/logout` zu Cloudflares
 * Abmeldung, die die Access-Sitzung beendet (sonst wäre man beim nächsten
 * Laden sofort wieder drin). Die Navigation fragt hier, ob sie den Eintrag
 * zeigt — früher schwebte der Knopf über dem Inhalt und verdeckte am Handy
 * „Speichern“.
 */
const LogoutContext = createContext<(() => void) | null>(null)

export function useLogout(): (() => void) | null {
  return useContext(LogoutContext)
}

function LogoutProvider({ children }: { children: ReactNode }) {
  const [weg, setWeg] = useState<'passwort' | 'access' | null>(null)
  useEffect(() => {
    void adminLoginMode()
      .then((r) => setWeg(r.passwordLogin ? 'passwort' : r.access ? 'access' : null))
      .catch(() => setWeg(null))
  }, [])
  const logout =
    weg === 'passwort'
      ? () => void adminLogout().then(() => window.location.reload())
      : weg === 'access'
        ? () => window.location.assign('/cdn-cgi/access/logout')
        : null
  return <LogoutContext.Provider value={logout}>{children}</LogoutContext.Provider>
}
